import { chunk, range } from "@common/helpers/collections";
import { BulkFieldReviewRequest } from "@common/schemas/transaction.schema";
import { TransactionReviewField } from "@common/types/transactionFieldReview";
import { TransactionsFilters } from "../filters/lib";

export const BULK_FIELD_REVIEW_LIMIT = 500;

interface BuildBulkFieldReviewRequestsParams {
    fields: TransactionReviewField[];
    transactionIds: number[];
    filtersSelection: TransactionsFilters | null;
    workspaceId: string | null | undefined;
    fallbackEntityIds: number[];
    transactionCount: number;
    dropsFromFilter: boolean;
}

export function reviewActionDropsFromFilter(
    filters: TransactionsFilters | null,
    fields: TransactionReviewField[],
    mode: "add" | "revoke",
): boolean {
    const filterFields =
        mode === "add" ? filters?.unreviewedFields : filters?.reviewedFields;
    if (!filterFields?.length) {
        return false;
    }
    const filterMode =
        mode === "add"
            ? (filters?.unreviewedFieldsMode ?? "every")
            : (filters?.reviewedFieldsMode ?? "any");
    return filterMode === "every"
        ? filterFields.some((field) => fields.includes(field))
        : filterFields.every((field) => fields.includes(field));
}

export function buildBulkFieldReviewRequests({
    fields,
    transactionIds,
    filtersSelection,
    workspaceId,
    fallbackEntityIds,
    transactionCount,
    dropsFromFilter,
}: BuildBulkFieldReviewRequestsParams): BulkFieldReviewRequest[] {
    if (filtersSelection) {
        if (!workspaceId || transactionCount === 0) {
            return [];
        }

        const entityIds =
            filtersSelection.entityIds && filtersSelection.entityIds.length > 0
                ? filtersSelection.entityIds
                : fallbackEntityIds;
        const pages = Math.ceil(transactionCount / BULK_FIELD_REVIEW_LIMIT);

        return range(pages).map((page) => ({
            fields,
            options: {
                workspaceId,
                filters: {
                    ...filtersSelection,
                    entityIds,
                },
                page: dropsFromFilter ? 0 : page,
                limit: BULK_FIELD_REVIEW_LIMIT,
            },
        }));
    }

    if (transactionIds.length === 0) {
        return [];
    }

    return chunk(transactionIds, BULK_FIELD_REVIEW_LIMIT).map((ids) => ({
        fields,
        transactionIds: ids,
    }));
}

export async function submitBulkFieldReviewRequests(
    requests: BulkFieldReviewRequest[],
    submit: (body: BulkFieldReviewRequest) => Promise<number>,
): Promise<number> {
    let total = 0;
    for (const request of requests) {
        total += await submit(request);
    }
    return total;
}
